import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { updatePaymentStatus } from "../api/payment";
import { XCircle } from "lucide-react";
import bookingApi from "../api/booking";
import { openPaymentCheckout } from "../utils/paymentUtils";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const PaymentFailed = () => {
  const query = useQuery();
  const orderId = query.get("order_id");
  const [status, setStatus] = useState("loading"); // loading, failed, error
  const [retryData, setRetryData] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const updateStatus = async () => {
      try {
        const res = await updatePaymentStatus(orderId, "FAILED");
        if (res?.retryData) {
          setRetryData(res.retryData);
        }
        setStatus("failed");
      } catch (err) {
        // Backend returns retryData even on failure path
        if (err?.retryData) {
          setRetryData(err.retryData);
        }
        setStatus("failed");
      }
    };
    if (orderId) {
      updateStatus();
    } else {
      setStatus("error");
    }
  }, [orderId]);

  const handleRetryPayment = async () => {
    if (isRetrying) return;
    if (!retryData) return;
    try {
      setIsRetrying(true);
      const res = await bookingApi.retryPayment(retryData);
      if (res.data?.success) {
        const { gatewayOrder } = res.data.data;
        if (!gatewayOrder) {
          alert("Error: Gateway order not returned from backend");
          setIsRetrying(false);
          return;
        }

        const handleReset = () => {
          setIsRetrying(false);
        };

        await openPaymentCheckout(gatewayOrder, handleReset, handleReset);
      } else {
        alert(res.data?.message || "Failed to retry payment.");
        setIsRetrying(false);
      }
    } catch (error) {
      console.error("Retry payment error:", error);
      alert(error.response?.data?.message || "An error occurred while retrying the payment.");
      setIsRetrying(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      {status === "loading" && (
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-red-400 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-lg text-gray-700">Processing payment failure...</p>
        </div>
      )}
      {status === "failed" && (
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full flex flex-col items-center">
          <div className="mb-6">
            <XCircle className="w-24 h-24 text-red-500" />
          </div>
          <h2 className="text-3xl font-bold text-[#1A0F3C] mb-3 text-center">
            Payment Failed
          </h2>
          <p className="text-gray-600 mb-6 text-center">
            Unfortunately, your payment could not be processed. Please try again
            or contact support for help.
          </p>
          <div className="bg-gray-50 rounded-lg p-3 shadow mb-6 w-full text-center">
            <span className="text-xs text-gray-500">Order ID:</span>
            <div className="font-mono text-sm text-red-500 break-all">
              {orderId}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            {retryData && (
              <button
                onClick={handleRetryPayment}
                disabled={isRetrying}
                className="flex-1 px-8 py-3 rounded-lg bg-[#FF4D12] text-white hover:bg-[#FF3D00] transition-colors inline-flex items-center justify-center gap-2 font-semibold shadow disabled:opacity-70"
              >
                {isRetrying ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Retrying...
                  </>
                ) : (
                  "Retry Payment"
                )}
              </button>
            )}
            <button
              onClick={() => navigate("/dashboard")}
              className="flex-1 px-8 py-3 rounded-lg bg-[#FF4D12] text-white hover:bg-[#FF3D00] transition-colors inline-flex items-center justify-center gap-2 font-semibold shadow"
            >
              Go to My Dashboard
              <span className="transform rotate-45">↗</span>
            </button>
          </div>
        </div>
      )}
      {status === "error" && (
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-10 h-10 text-gray-500"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">
            We couldn't update your payment status. Please contact support if
            you need help.
          </p>
        </div>
      )}
    </div>
  );
};

export default PaymentFailed;
